// src/components/site-editor/panels/ContactPanel.js
import React, { useState, useEffect } from 'react';
import { MessageSquare, AlertCircle } from 'lucide-react';
import InlineCallout from '../InlineCallout';
import Toggle from '../../Toggle';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';

const ContactPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const contactData = state.draft.contactFooter || {};

  // Local state for form fields
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [stateInput, setStateInput] = useState('');
  const [zip, setZip] = useState('');
  const [showEmail, setShowEmail] = useState(true);
  const [showPhone, setShowPhone] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);

  // Load existing data on mount
  useEffect(() => {
    if (contactData) {
      setEmail(contactData.email || '');
      setPhone(contactData.phone || '');
      setShowEmail(contactData.showEmail !== undefined ? contactData.showEmail : true);
      setShowPhone(contactData.showPhone !== undefined ? contactData.showPhone : false);
      setShowAddress(contactData.showAddress !== undefined ? contactData.showAddress : false);
      setShowContactForm(contactData.showContactForm !== undefined ? contactData.showContactForm : false);

      if (contactData.address) {
        setStreet(contactData.address.street || '');
        setCity(contactData.address.city || '');
        setStateInput(contactData.address.state || '');
        setZip(contactData.address.zip || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update context when fields change
  const handleEmailChange = (value) => {
    setEmail(value);
    actions.updateContactFooter('email', value);
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhoneNumber(value);
    setPhone(formatted);
    actions.updateContactFooter('phone', formatted);
  };

  const handleShowEmailToggle = () => {
    const newValue = !showEmail;
    setShowEmail(newValue);
    actions.updateContactFooter('showEmail', newValue);
  };

  const handleShowPhoneToggle = () => {
    const newValue = !showPhone;
    setShowPhone(newValue);
    actions.updateContactFooter('showPhone', newValue);
  };

  const handleShowAddressToggle = () => {
    const newValue = !showAddress;
    setShowAddress(newValue);
    actions.updateContactFooter('showAddress', newValue);
  };

  const handleShowContactFormToggle = () => {
    const newValue = !showContactForm;
    setShowContactForm(newValue);
    actions.updateContactFooter('showContactForm', newValue);
    // When form is enabled, disable email/phone display (form takes priority)
    if (newValue) {
      setShowEmail(false);
      setShowPhone(false);
      actions.updateContactFooter('showEmail', false);
      actions.updateContactFooter('showPhone', false);
    }
  };

  const handleAddressChange = (field, value) => {
    const updatedAddress = {
      street,
      city,
      state: stateInput,
      zip,
      [field]: value
    };

    // Update local state
    if (field === 'street') setStreet(value);
    if (field === 'city') setCity(value);
    if (field === 'state') setStateInput(value);
    if (field === 'zip') setZip(value);

    // Update context
    actions.updateContactFooter('address', updatedAddress);
  };

  // Check if any contact method is enabled
  const hasAnyContactMethod = showEmail || showPhone || showAddress || showContactForm;

  // Section title state
  const [sectionTitle, setSectionTitle] = useState('Get in Touch');

  // Load section title
  useEffect(() => {
    if (contactData.sectionTitle) {
      setSectionTitle(contactData.sectionTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSectionTitleChange = (value) => {
    setSectionTitle(value);
    actions.updateContactFooter('sectionTitle', value);
  };

  const predefinedTitles = [
    'Get in Touch',
    'Contact',
    "Let's Connect",
    'Reach Out',
    'Say Hello',
    'Contact Me'
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Information</h3>
        <p className="text-sm text-gray-600">
          Add your contact information. Toggle on what you want to show on your website.
        </p>
      </div>

      {/* Section Title */}
      <div>
        <label style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px', display: 'block', color: '#374151' }}>
          Section Title
        </label>
        <select
          value={predefinedTitles.includes(sectionTitle) ? sectionTitle : 'custom'}
          onChange={(e) => {
            if (e.target.value === 'custom') {
              handleSectionTitleChange('');
            } else {
              handleSectionTitleChange(e.target.value);
            }
          }}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#111827',
            background: '#FFFFFF',
            cursor: 'pointer',
            appearance: 'auto',
            boxSizing: 'border-box'
          }}
        >
          <option value="Get in Touch">Get in Touch</option>
          <option value="Contact">Contact</option>
          <option value="Let's Connect">Let's Connect</option>
          <option value="Reach Out">Reach Out</option>
          <option value="Say Hello">Say Hello</option>
          <option value="Contact Me">Contact Me</option>
          <option value="custom">Write my own...</option>
        </select>

        {/* Custom input — only shows when "Write my own" is selected */}
        {!predefinedTitles.includes(sectionTitle) && (
          <input
            type="text"
            value={sectionTitle}
            onChange={(e) => handleSectionTitleChange(e.target.value)}
            placeholder="Enter your custom title"
            autoFocus
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              marginTop: '8px',
              boxSizing: 'border-box'
            }}
          />
        )}
      </div>

      {/* Warning when nothing is toggled on */}
      {!hasAnyContactMethod && (
        <div style={{
          padding: '12px 16px',
          background: '#FEF3C7',
          border: '1px solid #FCD34D',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <AlertCircle size={20} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p className="text-sm font-medium text-gray-900 mb-1">
              Contact section hidden
            </p>
            <p className="text-xs text-gray-700">
              Toggle on at least one contact method to show the Contact section on your site.
            </p>
          </div>
        </div>
      )}

      {/* Inline Callout */}
      <InlineCallout
        text="Make it easy for potential clients to reach you. Even one clear contact method builds trust and encourages bookings."
        playbookTopic="building-your-presence"
      />

      {/* Email Address */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          placeholder="your@email.com"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />

        {/* Show Email Toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px',
          background: showContactForm ? '#F3F4F6' : '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          opacity: showContactForm ? 0.5 : 1,
          pointerEvents: showContactForm ? 'none' : 'auto',
        }}>
          <span className="text-sm text-gray-700">Show email on website</span>
          <Toggle enabled={showEmail} onChange={handleShowEmailToggle} />
        </div>

        <p className="text-xs text-gray-500">
          {showContactForm
            ? 'Email and phone are hidden when the contact form is active.'
            : 'When ON: email appears as a clickable mailto: link in the Contact section'}
        </p>
      </div>

      {/* Phone Number */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Phone Number
        </label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          placeholder="(555) 123-4567"
          maxLength={14}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />

        {/* Show Phone Toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px',
          background: showContactForm ? '#F3F4F6' : '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          opacity: showContactForm ? 0.5 : 1,
          pointerEvents: showContactForm ? 'none' : 'auto',
        }}>
          <span className="text-sm text-gray-700">Show phone on website</span>
          <Toggle enabled={showPhone} onChange={handleShowPhoneToggle} />
        </div>

        <p className="text-xs text-gray-500">
          {showContactForm
            ? 'Email and phone are hidden when the contact form is active.'
            : 'When ON: phone appears as a clickable tel: link in the Contact section'}
        </p>
      </div>

      {/* Business Address */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Business Location
        </label>

        {/* Street Address */}
        <input
          type="text"
          value={street}
          onChange={(e) => handleAddressChange('street', e.target.value)}
          placeholder="123 Main Street, Suite 4"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />

        {/* City - Full Width */}
        <input
          type="text"
          value={city}
          onChange={(e) => handleAddressChange('city', e.target.value)}
          placeholder="City"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />

        {/* State and Zip Row */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ position: 'relative', width: '90px', flexShrink: 0 }}>
            <select
              value={stateInput}
              onChange={(e) => handleAddressChange('state', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
                cursor: 'pointer',
                background: 'white',
                appearance: 'none',
                paddingRight: '28px'
              }}
            >
              <option value="">State</option>
              <option value="AL">AL</option>
              <option value="AK">AK</option>
              <option value="AZ">AZ</option>
              <option value="AR">AR</option>
              <option value="CA">CA</option>
              <option value="CO">CO</option>
              <option value="CT">CT</option>
              <option value="DE">DE</option>
              <option value="FL">FL</option>
              <option value="GA">GA</option>
              <option value="HI">HI</option>
              <option value="ID">ID</option>
              <option value="IL">IL</option>
              <option value="IN">IN</option>
              <option value="IA">IA</option>
              <option value="KS">KS</option>
              <option value="KY">KY</option>
              <option value="LA">LA</option>
              <option value="ME">ME</option>
              <option value="MD">MD</option>
              <option value="MA">MA</option>
              <option value="MI">MI</option>
              <option value="MN">MN</option>
              <option value="MS">MS</option>
              <option value="MO">MO</option>
              <option value="MT">MT</option>
              <option value="NE">NE</option>
              <option value="NV">NV</option>
              <option value="NH">NH</option>
              <option value="NJ">NJ</option>
              <option value="NM">NM</option>
              <option value="NY">NY</option>
              <option value="NC">NC</option>
              <option value="ND">ND</option>
              <option value="OH">OH</option>
              <option value="OK">OK</option>
              <option value="OR">OR</option>
              <option value="PA">PA</option>
              <option value="RI">RI</option>
              <option value="SC">SC</option>
              <option value="SD">SD</option>
              <option value="TN">TN</option>
              <option value="TX">TX</option>
              <option value="UT">UT</option>
              <option value="VT">VT</option>
              <option value="VA">VA</option>
              <option value="WA">WA</option>
              <option value="WV">WV</option>
              <option value="WI">WI</option>
              <option value="WY">WY</option>
              <option value="DC">DC</option>
            </select>
            {/* Dropdown arrow icon */}
            <div style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              color: '#9CA3AF',
              fontSize: '12px'
            }}>
              ▼
            </div>
          </div>
          <input
            type="text"
            value={zip}
            onChange={(e) => handleAddressChange('zip', e.target.value)}
            placeholder="Zip Code"
            maxLength={10}
            style={{
              width: 'calc(100% - 98px)',
              padding: '10px 12px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Show Address Toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px'
        }}>
          <span className="text-sm text-gray-700">Show address on website</span>
          <Toggle enabled={showAddress} onChange={handleShowAddressToggle} />
        </div>

        <p className="text-xs text-gray-500">
          When ON: address appears as a clickable Google Maps link in the Contact section
        </p>
      </div>

      {/* Contact Form */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Contact Form
        </label>

        {/* Show Contact Form Toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <MessageSquare size={20} style={{ color: '#6B7280', flexShrink: 0 }} />
            <span className="text-sm text-gray-700">Include contact form</span>
          </div>
          <Toggle enabled={showContactForm} onChange={handleShowContactFormToggle} />
        </div>

        <p className="text-xs text-gray-500">
          Let visitors send you a message without seeing your email address
        </p>
      </div>
    </div>
  );
};

export default ContactPanel;
